// Copyright (c) Docugami, Inc. All rights reserved.

import { AppState } from "../store";
import { useLocation, useHistory, useParams } from "react-router";
import { useEffect, useState } from "react";
import { useSelector} from "react-redux";
import { fetchOrganizationList, fetchBuilds } from "../store/organization/actions";
import { fetchClusters, setCurClusterAndEnsureDocumentsAndChunkTypes } from "../store/clusters/actions";
import { ensureDocumentState } from "../store/documents/actions";
import { useAppDispatch } from 'hooks/redux';
import { useDgAuth } from "./DgAuthentication";
import { useChunkSelection } from "./chunkSelection";

const clusterIdParam = 'clusterid';
const folderIdParam = "folderid";
const instanceIdParam = "instanceid";

// Contains the parameters, if present, as they get initialized
export interface InitializationContext {
    clusterId: string,
    documentId: string,
    instanceId: string
}

/**
 * Initializes global app state based on deep link parameters
 */
export function useDeepLinkInitializer(): [boolean, InitializationContext] {
    const params = useParams();

    function getParam(param: string): string
    {
        // @ts-ignore
        let res = params[param];
        res = res ? decodeURIComponent(res) : '';
        return res;
    }

    const initializerParams = {
        clusterId: getParam(clusterIdParam),
        documentId: getParam(folderIdParam),
        instanceId: getParam(instanceIdParam)
    }

    return useInitializer(initializerParams);
}

// Contains the values that should be initialized
export interface IntializerParams {
    clusterId?: string,
    documentId?: string,
    instanceId?: string
}

/**
 * Initializes global app state based on the given parameters
 * @param params The values that should be initialized
 */
export function useInitializer(params: IntializerParams): [boolean, InitializationContext]
{
    enum InitializerSteps {
        WaitingForUser = 0,
        FetchOrganizations,
        FetchingOrganizations,
        FetchGlobalStateForOrganization,
        FetchClusterIfSpecified,
        EnsureCurrentDocument,
        SetCurrentInstance,
        Ready,
        Error
    }

    const [inputParams, setInputParams] = useState(JSON.stringify(params));
    const [intializationContext, setInitializationContext] = useState<InitializationContext>({clusterId: '', documentId: '', instanceId: ''});
    const [isInitialized, setIsInitialized] = useState(false);

    const [initializationStep, setInitializationStep] = useState<InitializerSteps>(InitializerSteps.WaitingForUser);
    const { isAuthenticated } = useDgAuth();
    const { getAccessTokenSilently } = useDgAuth();
    const organizations = useSelector((state: AppState) => state.organizations);
    const clusters = useSelector((state: AppState) => state.clusters);
    const location = useLocation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const chunkSelection = useChunkSelection();

    useEffect(() => {
        const curParams = JSON.stringify(params);
        if (curParams !== inputParams) {
            setInputParams(curParams);
        }
    }, [params, inputParams]);

    useEffect(() => {
        setIsInitialized(initializationStep === InitializerSteps.Ready);
    }, [initializationStep, InitializerSteps.Ready])

    useEffect(() => {
        setInitializationStep(InitializerSteps.WaitingForUser);
        dispatch(ensureDocumentState(''));
        setInitializationContext({clusterId: '', documentId: '', instanceId: ''});
    }, [location.pathname, InitializerSteps.WaitingForUser, dispatch, inputParams])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        switch (initializationStep) {
            case InitializerSteps.WaitingForUser:
            {
                if (isAuthenticated) {
                    moveToStep(InitializerSteps.FetchOrganizations);
                    return;
                }
                return;
            }

            case InitializerSteps.FetchOrganizations:
            {
                if (!organizations.organizationList.fetchState.fetched &&
                    !organizations.organizationList.fetchState.fetching)
                {
                    dispatch(fetchOrganizationList(getAccessTokenSilently));
                }

                moveToStep(InitializerSteps.FetchingOrganizations);
                return;
            }

            case InitializerSteps.FetchingOrganizations:
            {
                if (organizations.organizationList.fetchState.fetched)
                {
                    if (organizations.organizationList.allIds.length <= 0) {
                        if (location.pathname !== "/") {
                            history.replace("/");
                        }

                        // if we have no organizations and already on landing, landing
                        // will take it from here
                        moveToStep(InitializerSteps.Ready);
                        return;
                    }

                    moveToStep(InitializerSteps.FetchGlobalStateForOrganization);
                    return;
                }

                return;
            }

            case InitializerSteps.FetchGlobalStateForOrganization:
            {
                if (clusters.fetched) {
                    // clusters is currently the only global state that we will wait
                    // for being moving on.  if there are dependencies between global
                    // state and other steps it should be checked here.
                    moveToStep(InitializerSteps.FetchClusterIfSpecified);
                    return;
                } else {
                    // these calls will protect themselves against multiple network
                    // calls if they are waiting for a response from a previous call
                    dispatch(fetchClusters());

                    if (!(window as any)._OfficeMode) {
                        dispatch(fetchBuilds());
                    }
                }

                return;
            }

            case InitializerSteps.FetchClusterIfSpecified:
            {
                const clusterId = params.clusterId;
                if (!clusterId) {
                    moveToStep(InitializerSteps.EnsureCurrentDocument);
                    return;
                }

                const clusterInfo = clusters.indexed[clusterId];
                if (!clusterInfo) {
                    console.error(`ClusterId not found in Clusters ${clusterId}`);
                    moveToStep(InitializerSteps.Error);
                    return;
                }

                if (clusterInfo.documentsFetchState.fetched) {
                    setInitializationContext(state => {
                        return {
                            ...state,
                            clusterId: clusterId
                        }
                    });
                    moveToStep(InitializerSteps.EnsureCurrentDocument);
                    return;
                } else if (!clusterInfo.documentsFetchState.fetching) {
                    const documentId = params.documentId;
                    if (!!documentId && !documentId.startsWith(clusterId)) {
                            console.error(`FolderId ${documentId} not valid for ClusterId ${clusterId}`);
                            moveToStep(InitializerSteps.Error);
                            return;
                    }

                    dispatch(setCurClusterAndEnsureDocumentsAndChunkTypes(clusterId));
                }

                return;
            }

            case InitializerSteps.EnsureCurrentDocument:
            {
                const documentId = params.documentId;
                if (!!documentId && documentId !== intializationContext.documentId) {
                    dispatch(ensureDocumentState(documentId));
                    setInitializationContext(state => {
                        return {
                            ...state,
                            documentId: documentId
                        }
                    });
                } else {
                    moveToStep(InitializerSteps.SetCurrentInstance);
                    return;
                }

                return;
            }

            case InitializerSteps.SetCurrentInstance:
            {
                const instanceId = params.instanceId;
                if (!!instanceId) {
                    if (!intializationContext.documentId) {
                        console.error(`Trying to load chunk instance ${instanceId} but no document was specified`);
                        moveToStep(InitializerSteps.Error);
                        return;
                    }

                    setInitializationContext(state => {
                        return {
                            ...state,
                            instanceId: instanceId
                        }
                    });

                    chunkSelection.selectChunk(instanceId);
                }

                moveToStep(InitializerSteps.Ready);
                return;
            }

            default:
                return;
        }
    })

    function moveToStep(step: InitializerSteps): void {
        console.log(`useDeepLinkInitializer: moving to initialization step ${step}`);
        setInitializationStep(step);
    }

    return [isInitialized, intializationContext];
}
